import React from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Grid,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import { observer } from "mobx-react";
import Calendar from "./Calendar";
import GoogleMapEmbed from "./GoogleMapEmbed";
import { useRoutingController } from "./controller/RoutingController";
import { UserDto } from "../../api-client";

interface RouteCalendarProps {
  initialUser?: UserDto;
  initialDate?: Date;
}

/**
 * RouteCalendar - A component for route planning and optimization.
 * 
 * Features:
 * - Two-column calendar view showing original and optimized schedules
 * - Google Maps integration for route visualization
 * - Drag and drop support for schedule optimization
 * 
 * Layout:
 * - Left side: Single calendar with two columns (Original Schedule | Optimized Schedule)  
 * - Right side: Google Maps showing optimized route with action buttons
 */
const RouteCalendar: React.FC<RouteCalendarProps> = observer(({
  initialUser,
  initialDate,
}) => {
  const controller = useRoutingController(initialUser, initialDate);

  if (!controller) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" height="100%">
        <Typography>Loading route planner...</Typography>
      </Box>
    );
  }

  const { data, handlers } = controller;

  const handleSave = async () => {
    // Save functionality not yet implemented
    // console.log("Save functionality not yet implemented");
  };

  const handleClose = () => {
    handlers.onCloseRouteplan();
  };

  return (
    <Grid container spacing={0} height="100%">
      {/* Left Side - Two-Column Calendar */}
      <Grid item xs={4} height="100%">
        <Calendar
          calendarEvents={data.combinedCalendarEvents}
          users={data.users}
          resources={data.resources}
          date={data.selectedDate}
          headerWidth="100%"
          onDatesSet={handlers.onDatesSet}
          onOutdated={handlers.onOutdated}
          onContinuousViewChange={() => {}} // Not used in route planning
          onCancelledViewChange={() => {}} // Not used in route planning
          lockedAppointments={data.lockedAppointments}
          groupCheck={false}
          // Only allow editing on optimized schedule
          onCreateEvent={handlers.onCreateEvent}
          onChangeEvent={handlers.onChangeEvent}
          onEventReceive={handlers.onEventReceive}
          onRemoveEvent={handlers.onRemoveEvent}
          onRemoveContinuousEvent={undefined}
          onMoveEventDate={handlers.onMoveEventDate as any}
          onAddShelfEvent={handlers.onAddShelfEvent as any}
          onEventContentClick={handlers.onEventContentClick}
          onCloseNotesDialog={() => {}}
          onSelectedShelfEventReceive={undefined}
        />
      </Grid>

      {/* Right Side - Google Maps with Action Buttons */}
      <Grid item xs={8} height="100%">
        <Box display="flex" flexDirection="column" height="100%" paddingTop="75px">
          <Box p={1} bgcolor="#f5f5f5" borderBottom="1px solid #ddd" display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="subtitle2" fontWeight="bold">
              Route Map
            </Typography>
            <Box display="flex" gap={1}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                disabled={true} // Deactivated since functionality is not yet supported
                size="small"
                title="Save functionality not yet implemented"
              >
                Save Route Plan
              </Button>
              <IconButton onClick={handleClose} size="small" title="Close route planning">
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
          <Box flex="1">
            {data.googleMapAppointments && data.googleMapAppointments.length > 0 ? (
              <GoogleMapEmbed appointments={data.googleMapAppointments} />
            ) : (
              <Box 
                display="flex" 
                alignItems="center" 
                justifyContent="center" 
                height="100%"
              >
                <Typography variant="body2" color="textSecondary">
                  Modify appointments in the optimized schedule to see route optimization
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Grid>
    </Grid>
  );
});

RouteCalendar.displayName = "RouteCalendar";

export default RouteCalendar;